import { SelectOptionProps } from '@acceso/design-system';
import { useMemo } from 'react';

import { useChannelScopeOptions } from '@/features/broadcasters/hooks/useChannelScopeOptions';
import { useLocationCodeToLabel } from '@/features/locations/hooks/useGetLocationLabelFromCode';

import { useAudienceChannelSources } from '../api/get-audience-channel-sources';
import { AudienceProviderEnum, ChannelMediaTypeEnum, ChannelScopeEnum } from '../types';

type Filters = {
  mediaType?: ChannelMediaTypeEnum | null;
  scope?: ChannelScopeEnum | null;
  countryCode?: string;
  regionCode?: string;
  name?: string;
  includeIds?: string[];

  /** Left out to list every provider's sources, as the broadcaster forms do. */
  providerIn?: AudienceProviderEnum[];
};

export function useAudienceChannelSourceOptions(filters?: Filters) {
  const { data: audienceChannelSources = [] } = useAudienceChannelSources({
    params: {
      mediaTypeIn: filters?.mediaType,
      name: filters?.name,
      includeIds: filters?.includeIds,
      providerIn: filters?.providerIn,
    },
  });

  const scopeOptions = useChannelScopeOptions();
  const locationCodeToLabel = useLocationCodeToLabel();

  const audienceChannelSourceOptions: SelectOptionProps<string>[] = useMemo(() => {
    const options = audienceChannelSources.map(src => {
      const scopeLabel =
        scopeOptions.find(opt => opt.value === (src.scope as unknown as ChannelScopeEnum))?.label ??
        src.scope;
      const countryLabel = locationCodeToLabel[src.country_code] ?? src.country_code;
      const regionLabel = src.region_code
        ? (locationCodeToLabel[src.region_code] ?? src.region_code)
        : null;
      const subregionLabel = src.subregion_code
        ? (locationCodeToLabel[src.subregion_code] ?? src.subregion_code)
        : null;

      const geoSuffix = [regionLabel, subregionLabel].filter(Boolean).join(', ');
      const label = geoSuffix
        ? `${src.name} (${scopeLabel}, ${countryLabel}, ${geoSuffix})`
        : `${src.name} (${scopeLabel}, ${countryLabel})`;

      return {
        label,
        value: src.id,
      };
    });

    options.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true })
    );

    return options;
  }, [
    audienceChannelSources,
    filters?.mediaType,
    filters?.scope,
    filters?.countryCode,
    filters?.regionCode,
    filters?.includeIds,
    filters?.providerIn,
    scopeOptions,
    locationCodeToLabel,
  ]);

  return audienceChannelSourceOptions;
}
